import type { SiteConfig } from '@dash-lite/sdk';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState  } from 'react';
import type {ReactNode} from 'react';
import { dashlite } from '@/lib/dashlite';
import { presentError  } from '@/lib/errors';
import type {ErrorPresentation} from '@/lib/errors';

/**
 * Configuration publique du site — `GET /site`.
 *
 * **C'est le premier appel que doit faire votre front**, une seule fois au
 * démarrage. Il rapporte en une requête tout ce dont les pages ont besoin :
 * le nom du site, son pays, l'environnement porté par la clé, l'adresse de
 * contact, et l'offre complète avec son tarif.
 *
 * L'alternative — interroger `GET /offer` sur chaque page qui affiche un prix
 * — multiplierait les allers-retours sans rien apporter : la réponse est
 * stable et porte des en-têtes de cache (le navigateur la conserve une minute
 * et reçoit ensuite un `304`).
 *
 * Ce contexte sert aussi de **garde de configuration** : tant que `GET /site`
 * n'a pas répondu, on ne sait pas si la clé est valide. `App` affiche donc un
 * écran de chargement, puis, en cas d'échec, un diagnostic explicite.
 */

type SiteContextValue = {
    /** `null` tant que le premier appel n'a pas abouti. */
    site: SiteConfig | null;
    loading: boolean;
    /** Erreur du dernier chargement, déjà traduite pour l'affichage. */
    error: ErrorPresentation | null;
    /** Relance `GET /site` — utilisé par le bouton « réessayer ». */
    reload: () => void;
};

const SiteContext = createContext<SiteContextValue | null>(null);

export function SiteProvider({ children }: { children: ReactNode }) {
    const [site, setSite] = useState<SiteConfig | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<ErrorPresentation | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            setSite(await dashlite.getSite());
        } catch (caught) {
            setError(presentError(caught));
            setSite(null);
        } finally {
            setLoading(false);
        }
    }, []);

    /**
     * `StrictMode` monte, démonte puis remonte chaque composant en
     * développement, afin de révéler les effets mal nettoyés. Sans cette
     * garde, `GET /site` partirait deux fois à chaque chargement de page —
     * inoffensif, mais trompeur dans l'inspecteur d'API. La référence
     * survit au démontage simulé, contrairement à l'état.
     */
    const initialLoadStarted = useRef(false);

    useEffect(() => {
        if (initialLoadStarted.current) {
            return;
        }

        initialLoadStarted.current = true;
        void load();
    }, [load]);

    const value = useMemo<SiteContextValue>(
        () => ({ site, loading, error, reload: () => void load() }),
        [site, loading, error, load],
    );

    return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
}

/** Accès au contexte brut — utilisé par la garde de démarrage de `App`. */
export function useSiteContext(): SiteContextValue {
    const context = useContext(SiteContext);

    if (!context) {
        throw new Error('useSiteContext doit être utilisé à l’intérieur de <SiteProvider>.');
    }

    return context;
}

/**
 * Accès à la configuration **garantie chargée**.
 *
 * `App` ne monte les pages qu'une fois `GET /site` abouti : les composants
 * peuvent donc lire `site.name` ou `site.offer` sans test préalable, ce qui
 * élimine des dizaines de `site?.` dans le code des pages.
 */
export function useSite(): SiteConfig {
    const { site } = useSiteContext();

    if (!site) {
        throw new Error(
            'useSite a été appelé avant que GET /site n’aboutisse. Les pages ne doivent être montées qu’après la garde de démarrage.',
        );
    }

    return site;
}
